import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ChevronLeft, ChevronRight, Sparkles, Award, Check } from 'lucide-react';
import CertificateEditor from './CertificateEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const Wizard = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    template_id: '',
    title: '',
    course_name: '',
    instructor_name: '',
    recipient_name: '',
    recipient_email: '',
    completion_date: new Date().toISOString().split('T')[0],
    template_layout: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [issuedCertificate, setIssuedCertificate] = useState(null);
  const [issuerSettings, setIssuerSettings] = useState(null);

  const totalSteps = 6;

  useEffect(() => {
    fetchIssuerSettings();
    fetchTemplates();
    loadFromLocalStorage();
  }, []);
  
  useEffect(() => {
    // Pre-populate instructor name with organization name from backend
    if (issuerSettings?.organization_name && !formData.instructor_name) {
      console.log('Setting instructor name from backend:', issuerSettings.organization_name);
      setFormData(prev => ({ ...prev, instructor_name: issuerSettings.organization_name }));
    }
  }, [issuerSettings]); // Run after issuer settings are loaded

  useEffect(() => {
    saveToLocalStorage();
  }, [formData]);

  const fetchIssuerSettings = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fetched issuer settings:', response.data);
      setIssuerSettings(response.data);
    } catch (error) {
      console.error('Error fetching issuer settings:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const templateData = response.data.items || response.data;
      const certificateTemplates = Array.isArray(templateData) ? templateData.filter(t => t.type === 'certificate') : [];
      console.log('Fetched templates:', certificateTemplates.length);
      setTemplates(certificateTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates. Please refresh and try again.');
    }
  };

  const saveToLocalStorage = () => {
    localStorage.setItem('wizard_progress', JSON.stringify(formData));
  };

  const loadFromLocalStorage = () => {
    const saved = localStorage.getItem('wizard_progress');
    if (saved) {
      try {
        setFormData(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading wizard progress:', e);
      }
    }
  };

  const handleSkip = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      await axios.put(`${API}/settings/wizard-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.removeItem('wizard_progress');
      if (onSkip) onSkip();
    } catch (error) {
      console.error('Error skipping wizard:', error);
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      // Auto-populate instructor name from backend settings if empty when moving from Step 3 to Step 4
      if (currentStep === 3 && !formData.instructor_name && issuerSettings?.organization_name) {
        setFormData(prev => ({ ...prev, instructor_name: issuerSettings.organization_name }));
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('issuer_token');
      
      // Step 1: Create certificate
      // CRITICAL: If user didn't customize layout in Step 4, use the template's default layout
      const layoutToSave = formData.template_layout || selectedTemplate?.layout || null;
      
      console.log('Creating certificate with layout:', layoutToSave ? 'Custom/Template layout' : 'No layout');
      
      const certResponse = await axios.post(`${API}/certificates`, {
        title: formData.title,
        course_name: formData.course_name,
        template_id: formData.template_id,
        instructor_name: formData.instructor_name || issuerSettings?.organization_name || '',
        template_layout: layoutToSave
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const certificateId = certResponse.data.id;

      // Step 2: Issue to recipient
      const recipientResponse = await axios.post(
        `${API}/recipients/single?certificate_id=${certificateId}`,
        {
          name: formData.recipient_name,
          email: formData.recipient_email,
          completion_date: formData.completion_date
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Step 3: Mark wizard complete
      await axios.put(`${API}/settings/wizard-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIssuedCertificate({
        serial: recipientResponse.data.serial_number,
        recipientName: formData.recipient_name
      });

      localStorage.removeItem('wizard_progress');
      setCurrentStep(6);
    } catch (error) {
      console.error('Error completing wizard:', error);
      if (error.response?.status === 403) {
        toast.error('Free tier limit reached. Please upgrade to continue.');
      } else {
        toast.error('Failed to issue certificate. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 2:
        return formData.template_id !== '';
      case 3:
        return formData.title && formData.course_name;
      case 5:
        return formData.recipient_name && formData.recipient_email;
      default:
        return true;
    }
  };

  const selectedTemplate = templates.find(t => t.id === formData.template_id);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-slate-900" />
            <h2 className="text-xl font-semibold text-slate-900">Certificate Wizard</h2>
          </div>
          <button
            onClick={handleSkip}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="px-8 py-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step < currentStep
                      ? 'bg-green-500 text-white'
                      : step === currentStep
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {step < currentStep ? <Check className="h-4 w-4" /> : step}
                </div>
                {step < 6 && (
                  <div
                    className={`w-12 h-1 mx-2 transition-all ${
                      step < currentStep ? 'bg-green-500' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="text-center max-w-2xl mx-auto">
              <Award className="h-16 w-16 text-slate-900 mx-auto mb-4" />
              <h3 className="text-3xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Let's create your first certificate!
              </h3>
              <p className="text-slate-600 mb-8">
                In just a few steps, you'll issue a professional certificate. Here's what we'll do:
              </p>
              <div className="text-left space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm flex-shrink-0 mt-0.5">1</div>
                  <div>
                    <p className="font-medium text-slate-900">Choose a template</p>
                    <p className="text-sm text-slate-600">Pick from 20+ professional designs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm flex-shrink-0 mt-0.5">2</div>
                  <div>
                    <p className="font-medium text-slate-900">Add your details</p>
                    <p className="text-sm text-slate-600">Course name, instructor, etc.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm flex-shrink-0 mt-0.5">3</div>
                  <div>
                    <p className="font-medium text-slate-900">Customize the layout</p>
                    <p className="text-sm text-slate-600">Drag and adjust elements (optional)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm flex-shrink-0 mt-0.5">4</div>
                  <div>
                    <p className="font-medium text-slate-900">Add your first recipient</p>
                    <p className="text-sm text-slate-600">Name and email address</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-8">
                Perfect for online courses, workshops, training programs, and certifications
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleNext}
                  className="px-8 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
                >
                  Get Started
                </button>
                <button
                  onClick={handleSkip}
                  className="px-8 py-3 bg-white text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Choose Template */}
          {currentStep === 2 && (
            <div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-2">Choose a Template</h3>
              <p className="text-slate-600 mb-6">Select a design that fits your style</p>
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Loading templates...</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setFormData({ ...formData, template_id: template.id })}
                    className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${
                      formData.template_id === template.id
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="aspect-[4/3] bg-slate-100 rounded mb-2 flex items-center justify-center overflow-hidden">
                      {template.background_url ? (
                        <img
                          src={`${API}/proxy-image?url=${encodeURIComponent(template.background_url)}`}
                          alt={template.name}
                          className="w-full h-full object-cover rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="flex items-center justify-center w-full h-full"><svg class="h-12 w-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg></div>';
                          }}
                        />
                      ) : (
                        <Award className="h-12 w-12 text-slate-400" />
                      )}
                    </div>
                    <p className="font-medium text-slate-900 text-sm">{template.name}</p>
                  </div>
                ))}
              </div>
              )}
              {templates.length > 0 && (
                <p className="text-sm text-slate-500 mt-4 text-center">
                  {templates.length} professional templates available
                </p>
              )}
            </div>
          )}

          {/* Step 3: Certificate Details */}
          {currentStep === 3 && (
            <div className="max-w-xl mx-auto">
              <h3 className="text-2xl font-semibold text-slate-900 mb-2">Certificate Details</h3>
              <p className="text-slate-600 mb-6">Fill in the information for your certificate</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Certificate Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Certificate of Completion"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Course Name *
                  </label>
                  <input
                    type="text"
                    value={formData.course_name}
                    onChange={(e) => setFormData({ ...formData, course_name: e.target.value })}
                    placeholder="e.g., Advanced Web Development"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Instructor Name
                  </label>
                  <input
                    type="text"
                    value={formData.instructor_name}
                    onChange={(e) => setFormData({ ...formData, instructor_name: e.target.value })}
                    placeholder="Your name or organization name"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    This will appear on all certificates as the instructor/issuer
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Customize Layout */}
          {currentStep === 4 && (
            <div>
              <h3 className="text-2xl font-semibold text-slate-900 mb-2 text-center">Customize Layout (Optional)</h3>
              <p className="text-slate-600 mb-4 text-center">Drag and adjust elements if you want. You can always edit this later.</p>
              {selectedTemplate && (
                <div>
                  <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 flex justify-center items-center overflow-hidden" style={{ minHeight: '600px', maxHeight: '600px' }}>
                    <div style={{ transform: 'scale(0.55)', transformOrigin: 'center center' }}>
                      <CertificateEditor
                        key={`preview-${formData.instructor_name}-${formData.course_name}-${currentStep}`}
                        templateBackground={selectedTemplate.background_url ? `${API}/proxy-image?url=${encodeURIComponent(selectedTemplate.background_url)}` : null}
                        onSave={(layout) => setFormData({ ...formData, template_layout: layout })}
                        logoPreview={null}
                        instructorName={formData.instructor_name || issuerSettings?.organization_name}
                        courseName={formData.course_name}
                        recipientName="Jane Smith"
                        completionDate={formData.completion_date}
                        serialNumber="PREVIEW-123"
                        initialLayout={selectedTemplate.layout}
                        isCustomBackground={true}
                        readOnly={false}
                      />
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      ✨ <strong>Current values:</strong> Instructor: <strong>{formData.instructor_name || issuerSettings?.organization_name || 'Not set'}</strong> | Course: <strong>{formData.course_name || 'Course Name'}</strong>
                    </p>
                  </div>
                </div>
              )}
              <p className="text-base text-slate-700 mt-4 text-center font-medium">
                💡 Tip: You can skip this step and customize later from your dashboard
              </p>
            </div>
          )}

          {/* Step 5: Add First Recipient */}
          {currentStep === 5 && (
            <div className="max-w-xl mx-auto">
              <h3 className="text-2xl font-semibold text-slate-900 mb-2">Add Your First Recipient</h3>
              <p className="text-slate-600 mb-6">Who should receive this certificate?</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Recipient Name *
                  </label>
                  <input
                    type="text"
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                    placeholder="e.g., John Doe"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.recipient_email}
                    onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                    placeholder="john@example.com"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Completion Date
                  </label>
                  <input
                    type="date"
                    value={formData.completion_date}
                    onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  />
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                💡 You can add more recipients or upload a CSV later from the dashboard
              </p>
            </div>
          )}

          {/* Step 6: Review & Success */}
          {currentStep === 6 && !issuedCertificate && (
            <div className="max-w-xl mx-auto text-center">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">Review & Issue</h3>
              <div className="bg-slate-50 rounded-lg p-6 text-left mb-6">
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-slate-500">Template:</span>
                    <p className="font-medium text-slate-900">{selectedTemplate?.name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500">Certificate Title:</span>
                    <p className="font-medium text-slate-900">{formData.title}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500">Course:</span>
                    <p className="font-medium text-slate-900">{formData.course_name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-slate-500">Recipient:</span>
                    <p className="font-medium text-slate-900">{formData.recipient_name} ({formData.recipient_email})</p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleComplete}
                disabled={isLoading}
                className="w-full px-8 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium disabled:opacity-50"
              >
                {isLoading ? 'Issuing Certificate...' : 'Issue Certificate'}
              </button>
            </div>
          )}

          {/* Success State */}
          {issuedCertificate && (
            <div className="max-w-xl mx-auto text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-2">Certificate Issued! 🎉</h3>
                <p className="text-slate-600 mb-4">
                  Your certificate has been issued! {issuedCertificate.recipientName} will receive an email with their certificate.
                </p>
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-slate-600 mb-2">Verification Link:</p>
                  <p className="text-sm font-mono text-slate-900 break-all">
                    {process.env.REACT_APP_BACKEND_URL?.replace('/api', '')}/verify/{issuedCertificate.serial}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-3 justify-center">
                <button
                  onClick={() => {
                    if (onComplete) onComplete();
                    navigate('/issuer/dashboard');
                  }}
                  className="px-8 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={() => {
                    window.open(`${window.location.origin}/verify/${issuedCertificate.serial}`, '_blank');
                  }}
                  className="px-8 py-3 bg-white text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  View Certificate (New Tab)
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        {currentStep > 1 && currentStep < 6 && !issuedCertificate && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-8 py-4 flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </button>
            <div className="flex gap-3">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                Skip wizard
              </button>
              {currentStep === 5 ? (
                <button
                  onClick={() => setCurrentStep(6)}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Review
                  <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="flex items-center gap-2 px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wizard;
